import React, { useState } from 'react';
import { TrendingUp, DollarSign, Building2, Zap, Users, CheckCircle, AlertCircle, BarChart3, LineChart } from 'lucide-react';

const EnterpriseAITracker = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  // December 2025 Enterprise AI Adoption Data
  const adoptionStats = {
    overallAdoption: {
      current: 78,
      previous: 55,
      change: 23,
      period: 'Nov 2024 - Nov 2025',
      source: 'McKinsey Nov 2025'
    },
    spending: {
      total: 37,
      growth: 3.2,
      previousYear: 11.5,
      source: 'Menlo Ventures Dec 2025'
    },
    productionRate: {
      rate: 47,
      comparison: 25,
      description: 'AI vs Traditional SaaS',
      source: 'Menlo Ventures Dec 2025'
    },
    agenticAI: {
      scaling: 23,
      experimenting: 39,
      total: 62,
      notYet: 67,
      source: 'McKinsey Nov 2025'
    }
  };

  const departmentSpending = [
    { department: 'Coding/Development', spend: 4.0, share: 55, description: 'GitHub Copilot, AI pair programming' },
    { department: 'Customer Support', spend: 2.8, share: 18, description: 'AI chatbots, ticket automation' },
    { department: 'Sales & Marketing', spend: 2.2, share: 15, description: 'Lead gen, content creation' },
    { department: 'HR & Recruiting', spend: 1.5, share: 8, description: 'Resume screening, interview scheduling' },
    { department: 'Finance & Operations', spend: 1.0, share: 4, description: 'Invoice processing, reporting' }
  ];

  const keyMilestones = [
    {
      date: 'Nov 2024',
      event: '55% Enterprise Adoption',
      impact: 'McKinsey baseline - AI tools spreading but not scaling'
    },
    {
      date: 'Dec 2024',
      event: 'Agentic AI Foundation Formed',
      impact: 'AWS, Google, Microsoft, Meta, NVIDIA, Anthropic standardize Model Context Protocol'
    },
    {
      date: 'Early 2025',
      event: 'Production Deployment Accelerates',
      impact: '47% AI projects reach production vs 25% traditional SaaS'
    },
    {
      date: 'Nov 2025',
      event: '78% Enterprise Adoption (+23%)',
      impact: 'McKinsey reports majority of organizations now using AI'
    },
    {
      date: 'Dec 2025',
      event: '$37B Enterprise Spending (3.2x)',
      impact: 'Menlo Ventures confirms rapid scaling from $11.5B'
    }
  ];

  const industryLeaders = [
    {
      industry: 'Financial Services',
      spend: 20,
      adoption: 68,
      use: 'Trading algorithms, risk assessment, fraud detection',
      companies: ['JPMorgan Chase', 'Goldman Sachs', 'Bank of America', 'Morgan Stanley']
    },
    {
      industry: 'Technology',
      spend: 8,
      adoption: 85,
      use: 'Product development, code generation, DevOps',
      companies: ['Microsoft', 'Google', 'Amazon', 'Meta', 'Salesforce']
    },
    {
      industry: 'Healthcare',
      spend: 4,
      adoption: 52,
      use: 'Diagnostic assistance, patient scheduling, documentation',
      companies: ['UnitedHealth', 'CVS Health', 'HCA Healthcare', 'Kaiser Permanente']
    },
    {
      industry: 'Retail',
      spend: 3,
      adoption: 61,
      use: 'Inventory optimization, customer service, pricing',
      companies: ['Walmart', 'Amazon', 'Target', 'Home Depot']
    },
    {
      industry: 'Manufacturing',
      spend: 2,
      adoption: 44,
      use: 'Quality control, predictive maintenance, supply chain',
      companies: ['General Electric', 'Ford', 'Boeing', 'Siemens']
    }
  ];

  const workforceImpact = [
    {
      metric: 'Jobs Requiring AI Skills',
      value: 67,
      trend: 'up',
      context: '2/3 of jobs now list AI as required or preferred'
    },
    {
      metric: 'Developers Using AI Daily',
      value: 50,
      trend: 'up',
      context: 'Half of developers use AI coding tools every workday'
    },
    {
      metric: 'Process Automation Adoption',
      value: 76,
      trend: 'up',
      context: '3/4 of enterprises automating routine processes'
    },
    {
      metric: 'AI Agent Deployments',
      value: 64,
      trend: 'up',
      context: 'Nearly 2/3 of companies deploying autonomous agents'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Enterprise AI Adoption Tracker</h1>
          <p className="text-gray-600 mb-4">
            Real-time intelligence on corporate AI deployment, spending, and workforce impact
          </p>
          <p className="text-sm text-gray-500">
            Last updated: December 2025 • Sources: McKinsey, Menlo Ventures, Industry Reports
          </p>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Overall Adoption */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <Building2 className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold text-green-600">+{adoptionStats.overallAdoption.change}%</span>
            </div>
            <div className="text-4xl font-bold text-gray-900 mb-1">
              {adoptionStats.overallAdoption.current}%
            </div>
            <div className="text-sm text-gray-600 mb-2">Enterprise Adoption</div>
            <div className="text-xs text-gray-500">
              Up from {adoptionStats.overallAdoption.previous}% in Nov 2024
            </div>
          </div>

          {/* Total Spending */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-green-600" />
              <span className="text-2xl font-bold text-green-600">{adoptionStats.spending.growth}x</span>
            </div>
            <div className="text-4xl font-bold text-gray-900 mb-1">
              ${adoptionStats.spending.total}B
            </div>
            <div className="text-sm text-gray-600 mb-2">2025 Spending</div>
            <div className="text-xs text-gray-500">
              Up from ${adoptionStats.spending.previousYear}B in 2024
            </div>
          </div>

          {/* Production Rate */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8 text-purple-600" />
              <span className="text-2xl font-bold text-purple-600">2x</span>
            </div>
            <div className="text-4xl font-bold text-gray-900 mb-1">
              {adoptionStats.productionRate.rate}%
            </div>
            <div className="text-sm text-gray-600 mb-2">Reach Production</div>
            <div className="text-xs text-gray-500">
              vs {adoptionStats.productionRate.comparison}% for traditional SaaS
            </div>
          </div>

          {/* Agentic AI */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <Zap className="w-8 h-8 text-orange-600" />
              <span className="text-2xl font-bold text-orange-600">{adoptionStats.agenticAI.total}%</span>
            </div>
            <div className="text-4xl font-bold text-gray-900 mb-1">
              {adoptionStats.agenticAI.scaling}%
            </div>
            <div className="text-sm text-gray-600 mb-2">Scaling Agents</div>
            <div className="text-xs text-gray-500">
              +{adoptionStats.agenticAI.experimenting}% experimenting
            </div>
          </div>
        </div>

        {/* Department Spending Breakdown */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            Spending by Department
          </h2>
          <div className="space-y-4">
            {departmentSpending.map((dept, idx) => (
              <div key={idx}>
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <span className="font-semibold text-gray-900">{dept.department}</span>
                    <span className="text-sm text-gray-600 ml-2">${dept.spend}B ({dept.share}%)</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 mb-1">
                  <div 
                    className="bg-blue-600 h-3 rounded-full transition-all"
                    style={{ width: `${dept.share}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500">{dept.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <LineChart className="w-6 h-6 text-green-600" />
            Adoption Timeline
          </h2>
          <div className="space-y-4">
            {keyMilestones.map((milestone, idx) => (
              <div key={idx} className="flex gap-4">
                <div className="flex-shrink-0 w-24 text-sm font-semibold text-gray-600">
                  {milestone.date}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 mb-1">{milestone.event}</div>
                  <p className="text-sm text-gray-600">{milestone.impact}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Industry Leaders */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-purple-600" />
            Industry Leaders
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {industryLeaders.map((industry, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-bold text-gray-900">{industry.industry}</h3>
                  <span className="text-lg font-bold text-blue-600">{industry.adoption}%</span>
                </div>
                <div className="text-sm text-gray-600 mb-2">
                  Annual Spend: <span className="font-semibold">${industry.spend}B+</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">{industry.use}</p>
                <div className="pt-3 border-t border-gray-100">
                  <div className="text-xs font-semibold text-gray-700 mb-1">Major Players:</div>
                  <div className="text-xs text-gray-600">
                    {industry.companies.join(', ')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Workforce Impact */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-red-600" />
            Workforce Impact
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {workforceImpact.map((item, idx) => (
              <div key={idx} className="border-l-4 border-red-600 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-3xl font-bold text-gray-900">{item.value}%</span>
                  <TrendingUp className="w-5 h-5 text-red-600" />
                </div>
                <div className="font-semibold text-gray-900 mb-1">{item.metric}</div>
                <p className="text-sm text-gray-600">{item.context}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Line Analysis */}
        <div className="bg-blue-50 rounded-lg p-6 mb-6">
          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600" />
            Bottom Line for Workers
          </h3>
          <div className="space-y-3 text-sm text-gray-700">
            <p>
              <strong>78% enterprise adoption</strong> means AI is no longer experimental—it's production infrastructure. 
              If your employer hasn't deployed AI yet, they're behind the curve.
            </p>
            <p>
              <strong>$37B spending (3.2x growth)</strong> indicates serious commitment. This isn't a bubble—companies 
              are integrating AI into core operations, not running pilots.
            </p>
            <p>
              <strong>47% production rate</strong> (vs 25% traditional SaaS) means AI projects succeed faster than 
              normal software. Deployment timelines are compressed.
            </p>
            <p>
              <strong>67% of jobs require AI skills</strong>—if your resume doesn't mention AI tools in your field, 
              you're invisible to recruiters. This isn't optional anymore.
            </p>
            <p className="font-semibold text-gray-900">
              Timeline: Most displacement hitting 2026-2027 as these 2025 deployments scale. You have 12-24 months 
              to position yourself on the right side of this transition.
            </p>
          </div>
        </div>

        {/* Data Sources */}
        <div className="bg-gray-100 rounded-lg p-6">
          <h3 className="font-bold text-gray-900 mb-3">Data Sources</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <span className="font-semibold text-gray-900 min-w-[180px]">McKinsey & Company:</span>
              <div>
                <a 
                  href="https://www.mckinsey.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Global Institute Reports
                </a>
                <span className="text-gray-600"> — Enterprise AI adoption surveys (Nov 2025)</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-semibold text-gray-900 min-w-[180px]">Menlo Ventures:</span>
              <div>
                <a 
                  href="https://menlovc.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  State of AI Reports
                </a>
                <span className="text-gray-600"> — Enterprise spending analysis (Dec 2025)</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-semibold text-gray-900 min-w-[180px]">Industry Reports:</span>
              <span className="text-gray-600">Financial services data from hedge fund surveys, technology sector from developer surveys, healthcare from CIO surveys</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-semibold text-gray-900 min-w-[180px]">OpenAI:</span>
              <div>
                <a 
                  href="https://openai.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Enterprise Usage Data
                </a>
                <span className="text-gray-600"> — 8x usage increase, 75% report output improvements</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-semibold text-gray-900 min-w-[180px]">Market Research:</span>
              <span className="text-gray-600">AI agent market projections, workforce AI skills requirements, developer tool adoption rates</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            Last updated: December 17, 2025 • Data compiled from public reports, industry surveys, and corporate earnings calls
          </p>
        </div>

      </div>
    </div>
  );
};

export default EnterpriseAITracker;